import { useSlideEditorContext } from "@/components/slides/editor/SlideEditorContext";
import {
  DeletePhonicsAudioInMediaHouseDocument,
  GetPhonicsAudiosByPhonemeDocument,
  type Phoneme,
  ReorderPhonicsAudioInMediaHouseDocument,
  UploadPhonicsAudioToMediaHouseDocument,
} from "@/generated/graphql";
import { useAddPhonicElement } from "@/lib/hooks/slides/editor/useAddPhonicElement";
import { useMediaHandler } from "@/lib/hooks/slides/editor/useMediaHandler";
import { Button } from "@ai-teach/ui-web";
import { useMutation, useQuery } from "@apollo/client/react";
import { useRef, useState } from "react";
import toast from "react-hot-toast";
import { FaSync } from "react-icons/fa";
import { HiUpload } from "react-icons/hi";
import { IoClose } from "react-icons/io5";
import { VoiceVariantCard } from "./VoiceVariantCard";

interface VoicesDialogProps {
  isOpen: boolean;
  onClose: () => void;
  phoneme: Phoneme;
  onSelect?:
    | ((
        media: { src: string; id: string; filename: string },
        phonemeName: string,
        phonemeId: string
      ) => void)
    | undefined;
}

export function VoicesDialog({ isOpen, onClose, phoneme, onSelect }: VoicesDialogProps) {
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { copying, copyFromMediaHouse } = useMediaHandler();
  const { handleAddPhonicElement } = useAddPhonicElement();
  const { setActivePanel } = useSlideEditorContext();

  const { data, loading, error, refetch } = useQuery(GetPhonicsAudiosByPhonemeDocument, {
    variables: { phonemeId: phoneme.id },
    skip: !isOpen,
    fetchPolicy: "cache-and-network",
  });

  const [deleteAudio] = useMutation(DeletePhonicsAudioInMediaHouseDocument);
  const [reorderAudio] = useMutation(ReorderPhonicsAudioInMediaHouseDocument);
  const [uploadAudio] = useMutation(UploadPhonicsAudioToMediaHouseDocument);

  if (!isOpen) return null;

  const audios = data?.phonicsAudiosByPhonemeFromMediaHouse ?? [];

  const handleAddVoice = async (audioId: string) => {
    const mediaAsset = await copyFromMediaHouse(audioId, phoneme.id);
    if (onSelect) {
      onSelect(mediaAsset, phoneme.phoneme, phoneme.id);
    } else {
      handleAddPhonicElement(mediaAsset, phoneme);
    }
    setActivePanel(null);
    onClose();
  };

  const handleTogglePlay = (audioId: string) => {
    setPlayingId((prev) => (prev === audioId ? null : audioId));
  };

  const handleDelete = async (audioId: string) => {
    setDeletingId(audioId);
    try {
      await deleteAudio({ variables: { id: audioId } });
      await refetch();
      toast.success("Audio deleted");
    } catch {
      toast.error("Failed to delete audio");
    } finally {
      setDeletingId(null);
    }
  };

  const handleReorder = async (audioId: string, direction: "up" | "down") => {
    const idx = audios.findIndex((a) => a.id === audioId);
    if (idx === -1) return;
    const newOrder = [...audios.map((a) => a.id)];
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= newOrder.length) return;
    const tmp = newOrder[idx];
    newOrder[idx] = newOrder[swapIdx] as string;
    newOrder[swapIdx] = tmp as string;
    try {
      await reorderAudio({ variables: { phonemeId: phoneme.id, orderedIds: newOrder } });
      await refetch();
    } catch {
      toast.error("Failed to reorder audio");
    }
  };

  const handleUpload = async (files: File[]) => {
    const audioFiles = files.filter((f) => f.type.startsWith("audio/"));
    if (audioFiles.length === 0) {
      toast.error("Only audio files can be uploaded");
      return;
    }
    setUploading(true);
    try {
      // sequential so the resulting variants keep the dropped order
      for (const file of audioFiles) {
        await uploadAudio({ variables: { file, phonemeId: phoneme.id } });
      }
      await refetch();
      toast.success(
        audioFiles.length > 1 ? `${audioFiles.length} audios uploaded` : "Audio uploaded"
      );
    } catch {
      toast.error("Failed to upload audio");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (uploading) return;
    handleUpload(Array.from(e.dataTransfer.files));
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDragging(false);
        }}
        onDrop={handleDrop}
        className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[85vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h2 className="text-xl font-bold">Phoneme: {phoneme.phoneme}</h2>
            <p className="text-sm text-slate-500 capitalize">
              {phoneme.phonemeType.replace(/_/g, " ")}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-full transition-colors"
          >
            <IoClose size={24} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-sm text-slate-500">Loading voice recordings...</div>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="text-sm text-red-600 mb-2">Failed to load voice recordings</div>
              <div className="text-xs text-slate-500 mb-4">{error.message}</div>
              <Button
                onClick={() => refetch()}
                variant="primary"
                size="sm"
                className="flex items-center gap-2"
              >
                <FaSync size={12} />
                Retry
              </Button>
            </div>
          ) : audios.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <p className="text-sm text-slate-500">No voice recordings yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {audios.map((audio, idx) => (
                <VoiceVariantCard
                  key={audio.id}
                  audio={audio}
                  isPlaying={playingId === audio.id}
                  onTogglePlay={() => handleTogglePlay(audio.id)}
                  onAdd={() => handleAddVoice(audio.id)}
                  copying={copying}
                  onDelete={() => handleDelete(audio.id)}
                  deleting={deletingId === audio.id}
                  onMoveUp={() => handleReorder(audio.id, "up")}
                  onMoveDown={() => handleReorder(audio.id, "down")}
                  isFirst={idx === 0}
                  isLast={idx === audios.length - 1}
                />
              ))}
            </div>
          )}
        </div>

        {/* Upload footer */}
        <div className="p-4 border-t bg-slate-50 rounded-b-lg">
          <p className="text-xs text-slate-500 mb-2 font-medium uppercase tracking-wide">
            Upload new voice
          </p>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className={`w-full flex flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed px-4 py-5 transition-colors ${
              isDragging
                ? "border-primary-500 bg-primary-50"
                : "border-slate-300 bg-white hover:border-primary-400 hover:bg-primary-50/40"
            } disabled:cursor-not-allowed disabled:opacity-60`}
          >
            <HiUpload
              size={20}
              className={`text-primary-600 pointer-events-none ${uploading ? "animate-bounce" : ""}`}
            />
            <span className="text-sm font-medium text-slate-700 pointer-events-none">
              {uploading
                ? "Uploading..."
                : isDragging
                  ? "Drop to upload"
                  : "Drop audio here or click to browse"}
            </span>
            <span className="text-xs text-slate-400 pointer-events-none">
              MP3, WAV, OGG — multiple files supported
            </span>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="audio/*"
            multiple
            className="hidden"
            onChange={(e) => {
              const files = Array.from(e.target.files ?? []);
              if (files.length > 0) handleUpload(files);
            }}
            disabled={uploading}
          />
        </div>
      </div>
    </div>
  );
}
